/**
 * viewConfig.js - what a saved table view holds, and the pure helpers that
 * turn it into columns on screen (10/9/26). Shared by the Projects and
 * Opportunities tables (useTableView.js).
 *
 * A view's config, as stored by server/routes/tableViews.js:
 *   columns    every column id, in the order the person put them
 *   hidden     the ids they switched off
 *   filters    { colId: [values] | { from, to } | { min, max } } - the column
 *              filters, status chips included (a Set is stored as a list)
 *   sort       { col, dir }
 *   search     the search box text
 *   highlights [rule] - see highlights.js
 *   widths     { colId: px } - columns the person resized (round 2, 10/9/26)
 *   fonts      { colId: { head?, cell? } } - px font sizes for a column's
 *              header and its cells (round 3, 10/9/26)
 *   hover      { on, color } - the whole-row hover (round 3; highlights.js)
 *   align      { colId: 'left' | 'center' | 'right' } - a column's alignment (round 4)
 *   pinned     [colId] - columns frozen at the left while the table scrolls sideways
 *   look       how the table looks (round 4, the Customize revamp) - DEFAULT_LOOK:
 *                density  'compact' | 'comfortable' | 'spacious' (row height)
 *                fontSize the table's own text size in px (null = 10.5)
 *                zebra    alternate row shading     gridlines  vertical cell lines
 *                header   'band' (pale green) | 'light'
 *                wrap     long text wraps (true) or ends in "…" (false)
 *                totals   'both' | 'top' | 'bottom' | 'none' - where the totals rows show
 *                sticky   the header stays on screen while the rows scroll
 *                group    records grouped under their main project / sub-project
 *                         (10/14/26, data/projectHierarchy.js) - on by default
 *   tree       how the main project / sub-project rows look (10/14/26) - treeLook.js
 * A view saved before a field existed gets its default, so old views keep
 * looking as they did.
 */
import { cleanTree } from './treeLook';

export const DENSITIES = ['compact', 'comfortable', 'spacious'];
export const TOTALS_PLACES = ['both', 'top', 'bottom', 'none'];
export const ALIGNS = ['left', 'center', 'right'];
export const MAX_PINNED = 6;
export const BASE_FONTS = [9, 9.5, 10, 10.5, 11, 11.5, 12, 13, 14];
export const DEFAULT_LOOK = Object.freeze({
  density: 'compact',
  fontSize: null,
  zebra: true,
  gridlines: true,
  header: 'band',
  wrap: true,
  totals: 'both',
  sticky: true,
  group: true,
});

/** A view's look with every field present and valid. PURE. */
export function cleanLook(look) {
  const l = look && typeof look === 'object' ? look : {};
  const bool = (v, d) => (typeof v === 'boolean' ? v : d);
  const fs = Number(l.fontSize);
  return {
    density: DENSITIES.includes(l.density) ? l.density : DEFAULT_LOOK.density,
    fontSize:
      l.fontSize != null && l.fontSize !== '' && Number.isFinite(fs) && fs > 0 ? Math.min(14, Math.max(9, Math.round(fs * 2) / 2)) : null,
    zebra: bool(l.zebra, DEFAULT_LOOK.zebra),
    gridlines: bool(l.gridlines, DEFAULT_LOOK.gridlines),
    header: l.header === 'light' ? 'light' : 'band',
    wrap: bool(l.wrap, DEFAULT_LOOK.wrap),
    totals: TOTALS_PLACES.includes(l.totals) ? l.totals : DEFAULT_LOOK.totals,
    sticky: bool(l.sticky, DEFAULT_LOOK.sticky),
    group: bool(l.group, DEFAULT_LOOK.group),
  };
}

/** Only known columns with a real alignment. */
export function cleanAlign(defs, align) {
  const known = new Set(defs.map((d) => d.col));
  const out = {};
  for (const [c, a] of Object.entries(align || {})) if (known.has(c) && ALIGNS.includes(a)) out[c] = a;
  return out;
}

/** Known columns, once each, at most MAX_PINNED. */
export function cleanPinned(defs, pinned) {
  const known = new Set(defs.map((d) => d.col));
  return (Array.isArray(pinned) ? pinned : []).filter((c, i, a) => known.has(c) && a.indexOf(c) === i).slice(0, MAX_PINNED);
}

// A resized column stays between these (px); the server clamps the same.
export const MIN_WIDTH = 40;
export const MAX_WIDTH = 600;
export const clampWidth = (px) => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(Number(px) || 0)));

// Font sizes (px) a column's header or cells may take; the server clamps the same.
export const MIN_FONT = 9;
export const MAX_FONT = 18;
export const FONT_SIZES = Array.from({ length: MAX_FONT - MIN_FONT + 1 }, (_, i) => MIN_FONT + i);
export const clampFont = (px) => Math.min(MAX_FONT, Math.max(MIN_FONT, Math.round(Number(px) || 0)));

/** Only the known columns' sizes, clamped; a column with neither size is left out. */
export function cleanFonts(defs, fonts) {
  const known = new Set(defs.map((d) => d.col));
  const out = {};
  for (const [c, f] of Object.entries(fonts || {})) {
    if (!known.has(c) || !f || typeof f !== 'object') continue;
    const one = {};
    if (Number(f.head) > 0) one.head = clampFont(f.head);
    if (Number(f.cell) > 0) one.cell = clampFont(f.cell);
    if (one.head || one.cell) out[c] = one;
  }
  return out;
}

/**
 * How the table is laid out. With no widths set it is the usual auto layout.
 * Once the person has resized a column every column has a width (the first
 * resize records what was on screen), and the table is fixed-layout at
 * exactly those widths (index.css .tv-fixed: what does not fit ends in an
 * ellipsis). A column shown later, with no width of its own, gets
 * defaultWidth. `extra` is the Actions column's width.
 */
export const defaultWidth = (def) => Math.round((def.minWidth || 80) * 1.2);
export function tableLayout(visible, widths, extra = 0) {
  const w = widths || {};
  if (!Object.keys(w).length) return { fixed: false, widthOf: () => undefined, total: undefined, style: undefined };
  const widthOf = (c) => w[c.col] || defaultWidth(c);
  const total = visible.reduce((n, c) => n + widthOf(c), 0) + extra;
  // At least the container's width (round 4: a saved view narrower than the
  // screen left a blank area beside the table): the browser spreads the
  // spare width over the data columns in proportion. The Actions column
  // (`extra`) is sized to its buttons by the table and never squeezed.
  return { fixed: true, widthOf, total, style: { width: total, minWidth: '100%' } };
}

/** Only the known columns' widths, clamped. */
export function cleanWidths(defs, widths) {
  const known = new Set(defs.map((d) => d.col));
  const out = {};
  for (const [c, w] of Object.entries(widths || {})) if (known.has(c) && Number(w) > 0) out[c] = clampWidth(w);
  return out;
}

/** The columns in the view's order, and the ones showing. A column the view has never heard of (added to the app later) shows at the end. */
export function resolveColumns(defs, layout) {
  const byId = new Map(defs.map((d) => [d.col, d]));
  const order = (layout && Array.isArray(layout.columns) ? layout.columns : []).filter((c, i, a) => byId.has(c) && a.indexOf(c) === i);
  for (const d of defs) if (!order.includes(d.col)) order.push(d.col);
  const hidden = new Set((layout && layout.hidden) || []);
  let visible = order.filter((c) => !hidden.has(c));
  if (!visible.length) visible = order.slice(0, 1); // never an empty table
  // frozen columns come first, in their own order, so they can stick at the left
  const pinned = new Set(cleanPinned(defs, layout && layout.pinned));
  visible = [...visible.filter((c) => pinned.has(c)), ...visible.filter((c) => !pinned.has(c))];
  return { order, visible: visible.map((c) => byId.get(c)) };
}

/** columnFilters (Sets and ranges) -> plain JSON for a view. Empty filters are left out. */
export function serializeFilters(filters) {
  const out = {};
  for (const [col, v] of Object.entries(filters || {})) {
    if (!v) continue;
    if (v instanceof Set) {
      if (v.size) out[col] = [...v].map(String).sort();
      continue;
    }
    if (Array.isArray(v)) {
      if (v.length) out[col] = v.map(String).sort();
      continue;
    }
    if (v.from || v.to) out[col] = { from: v.from || null, to: v.to || null };
    else if (v.min != null || v.max != null) out[col] = { min: v.min ?? null, max: v.max ?? null };
  }
  return out;
}

/** A view's filters -> the columnFilters shape the tables use (lists become Sets). */
export function deserializeFilters(filters) {
  const out = {};
  for (const [col, v] of Object.entries(filters || {})) {
    if (Array.isArray(v)) out[col] = new Set(v.map(String));
    else if (v && typeof v === 'object') out[col] = { ...v };
  }
  return out;
}

/** Key order and list order fixed, so two configs that show the same table compare equal. */
function canonical(config, defs) {
  const c = config || {};
  const { order, visible } = resolveColumns(defs, c);
  const filters = serializeFilters(c.filters instanceof Object ? deserializeFilters(c.filters) : {});
  return JSON.stringify({
    order,
    visible: visible.map((d) => d.col),
    filters: Object.keys(filters)
      .sort()
      .map((k) => [k, filters[k]]),
    sort: c.sort ? [c.sort.col, c.sort.dir] : null,
    search: (c.search || '').trim(),
    highlights: (c.highlights || []).map((r) => [
      r.col,
      r.op,
      r.value ?? null,
      r.color ?? null,
      r.font ?? null,
      !!r.bold,
      r.scope,
      !!r.off,
    ]),
    align: Object.entries(cleanAlign(defs, c.align)).sort(([x], [y]) => (x < y ? -1 : 1)),
    pinned: cleanPinned(defs, c.pinned),
    look: cleanLook(c.look),
    tree: cleanTree(c.tree),
    widths: Object.entries(cleanWidths(defs, c.widths)).sort(([x], [y]) => (x < y ? -1 : 1)),
    hover: [!(c.hover && c.hover.on === false), String((c.hover && c.hover.color) || '#D1FAE5').toUpperCase()],
    fonts: Object.entries(cleanFonts(defs, c.fonts))
      .sort(([x], [y]) => (x < y ? -1 : 1))
      .map(([k, f]) => [k, f.head || 0, f.cell || 0]),
  });
}

/** Do two configs show the same table? (Drives the "modified" dot.) */
export function sameConfig(a, b, defs) {
  return canonical(a, defs) === canonical(b, defs);
}
